'use client';

import { useCallback } from 'react';
import { PROMO_BANNER_HEIGHT_CLASS } from '../../hooks';
import { useOnboardingContext } from '../../providers/OnboardingProvider';

interface OnboardingBannerProps {
  type: 'top' | 'hero';
}

export default function OnboardingBanner({ type }: OnboardingBannerProps) {
  const {
    startOnboarding,
    closeBanner,
    shouldShowTopBanner,
    shouldShowHeroBanner,
    userType,
    isProviderContext,
  } = useOnboardingContext();

  const bannerType = type;

  const handleSignupClick = useCallback(() => {
    startOnboarding();
  }, [startOnboarding]);

  // Use specific trigger conditions for each banner type
  const shouldShowBanner = bannerType === 'top' ? shouldShowTopBanner : shouldShowHeroBanner;

  if (!shouldShowBanner) {
    return null;
  }

  // Customize text based on user type
  const bannerText = isProviderContext
    ? 'Help your patients find the right treatment programs.'
    : 'Ready to find the right treatment?';

  const buttonText = 'Sign Up & Upload';
  const descriptionText = 'Upload medical records & instantly get programs matches.';

  return bannerType === 'top' ? (
    <TopBanner
      bannerText={bannerText}
      descriptionText={descriptionText}
      buttonText={buttonText}
      handleSignupClick={handleSignupClick}
      handleClose={closeBanner}
    />
  ) : (
    <UploadPrompt handleSignupClick={handleSignupClick} />
  );
}

interface TopBannerProps {
  bannerText: string;
  descriptionText: string;
  buttonText: string;
  handleSignupClick: () => void;
  handleClose: () => void;
}
const TopBanner = ({
  bannerText,
  descriptionText,
  buttonText,
  handleSignupClick,
  handleClose,
}: TopBannerProps) => {
  return (
    <div
      className={`fixed top-0 left-0 right-0 w-full bg-primary text-white px-4 z-[51] flex items-center justify-between ${PROMO_BANNER_HEIGHT_CLASS}`}
    >
      {/* Text content aligned to the left, taking available space */}
      <div className="flex-1 flex items-center space-x-2 sm:space-x-4 overflow-hidden">
        <div className="flex flex-col text-left">
          <p className="text-sm text-white truncate">
            <span className="font-semibold text-white">{bannerText}</span> {descriptionText}
          </p>
        </div>
      </div>

      {/* Button and Close icon aligned to the right */}
      <div className="flex items-center ml-2 sm:ml-4 flex-shrink-0">
        <button
          type="button"
          onClick={handleSignupClick}
          className="bg-background text-black hover:bg-gray-100 font-semibold text-xs sm:text-sm px-2 sm:px-3 py-2 rounded-md whitespace-nowrap mr-2 sm:mr-3"
        >
          {buttonText}
        </button>
        <button
          onClick={handleClose}
          className="p-1 text-white hover:text-gray-400"
          aria-label="Close banner"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="#fff"
            className="w-4 h-4"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

interface UploadPromptProps {
  handleSignupClick: () => void;
}

const UploadPrompt = ({ handleSignupClick }: UploadPromptProps) => {
  return (
    <div key="upload-prompt" className="py-8 bg-gray-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-6 lg:gap-8">
          <div className="lg:col-span-4 self-center">
            <h3 className="mb-0 mt-1 text-left whitespace-nowrap text-black">
              Get Instantly Matched
            </h3>
          </div>
          <div className="lg:col-span-5 self-center">
            <p className="text-black text-left">
              Upload your <span className="font-semibold">medical records</span>, and our platform
              will quickly match you with suitable treatment programs.
            </p>
          </div>
          <div className="lg:col-span-3 flex lg:justify-end self-center">
            <button
              type="button"
              className="font-semibold bg-black text-white hover:bg-gray-900 px-6 py-3 rounded-full"
              onClick={handleSignupClick}
            >
              Upload Records & Find Matches
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
